import classNames from 'classnames';
import DOMPurify from 'dompurify';
import React, {ReactElement} from 'react';

import {assert} from 'Asserts';
import * as ToastActions from 'nav/toasts/ToastActions';
import {KEEP_TOAST_OPEN, ToastAction, ToastId, ToastType} from 'nav/toasts/ToastConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Clipboard from 'ui-library/Clipboard';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface NormalizedProps {
  id: ToastId;
  actions: Array<ToastAction>;
  isCloseable?: boolean;
  // When pushing a toast with an ID, message may be omitted, in which case the same message as
  // before is used.
  message?: string | ReactElement;
  onClose?: () => void;
  progress?: number;
  skipCloseCallbackOnActions: boolean;
  type: ToastType;
}
// New uses of Toast should specify actions in an array; however for backwards compatibility we also support
// specifying a single action by providing the parameters as top-level props
type Props = Omit<NormalizedProps, 'actions' | 'skipCloseCallbackOnActions'> &
  ToastAction & {actions?: Array<ToastAction>; skipCloseCallbackOnActions?: boolean};

const BUTTON_STYLES_MAP: {[toastType in ToastType]: ButtonStyle} = {
  [ToastType.SUCCESS]: ButtonStyle.SUCCESS,
  [ToastType.INFO]: ButtonStyle.SECONDARY,
  [ToastType.WARNING]: ButtonStyle.WARNING,
  [ToastType.DANGER]: ButtonStyle.WARNING,
};

function ActionButton({
  action,
  toastType,
  onClick,
}: {
  action: ToastAction;
  toastType: ToastType;
  onClick: (action: ToastAction) => void;
}): JSX.Element {
  return (
    <Button
      buttonStyle={BUTTON_STYLES_MAP[toastType]}
      className="toast-action"
      size={ButtonSize.SMALL}
      text={action.clipboardData ? 'Copy' : action.actionText}
      onClick={() => {
        onClick(action);
      }}
      dataTestAttrs={{component: 'Toast', element: 'actionButton'}}
    />
  );
}

/**
 * Historically, we supported exactly one action per toast. New toasts should use an array of action structs.
 * This function munges legacy inline single action props into the struct-array form, as well as defaulting
 * an optional bool prop to an explicitly false value if nullish.
 */
function _normalizeProps(props: Props): NormalizedProps {
  const {
    actionText,
    onActionClick,
    clipboardData,
    actions = [],
    skipCloseCallbackOnActions = false,
    ...otherProps
  } = props;
  assert(
    !(actions.length > 0 && (actionText || onActionClick || clipboardData)),
    'Toast received both an actions array and actionText/onActionClick/clipboardData as newProps'
  );

  let newActions = actions;
  if ((actionText && onActionClick) || clipboardData) {
    newActions = [
      {
        actionText,
        clipboardData,
        onActionClick,
      },
    ];
  }

  return {...otherProps, actions: newActions, skipCloseCallbackOnActions};
}

export default function Toast(rawProps: Props): JSX.Element {
  const props = _normalizeProps(rawProps);

  function _handleClose(): void {
    if (props.onClose != null) {
      props.onClose();
    }
    ToastActions.closeToast(props.id);
  }

  function _handleActionClick(action: ToastAction): void {
    if (action.onActionClick) {
      const result = action.onActionClick();
      if (result === KEEP_TOAST_OPEN) {
        return;
      }
    }
    if (props.skipCloseCallbackOnActions) {
      ToastActions.closeToast(props.id);
    } else {
      _handleClose();
    }
  }

  function _handleNotifyCopied(): void {
    ToastActions.pushToast({
      message: 'Successfully copied to clipboard.',
      type: ToastType.SUCCESS,
    });
  }

  let message = props.message;
  let progressString;
  if (props.progress != null) {
    if (props.progress === TrayTaskConstants.INDETERMINATE_PROGRESS) {
      progressString = '...';
    } else {
      progressString = ` (${props.progress.toFixed(0)}%)`;
    }
    if (typeof message === 'string') {
      message += progressString;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options: {[key: string]: any} = {};
  if (typeof message === 'string') {
    options.dangerouslySetInnerHTML = {__html: DOMPurify.sanitize(message)};
  } else {
    options.children = [
      <span key="message">
        {message}
        {progressString}
      </span>,
    ];
  }

  return (
    <div
      className={classNames(`toast toast--${props.type}`, {
        'toast--actionable': props.actions.length > 0,
        'toast--twoActions': props.actions.length > 1,
      })}
      {...getTestProps({
        component: 'Toast',
        element: 'root',
        key: props.type,
      })}
      data-amp-safe={false}
    >
      <div className="toast-text" {...getTestProps({component: 'Toast', element: 'mainText'})} {...options} />
      {props.actions.map((action, i) => {
        if (action.actionText && action.onActionClick) {
          return <ActionButton key={i} toastType={props.type} action={action} onClick={_handleActionClick} />;
        } else if (action.clipboardData) {
          return (
            <Clipboard key={i} notifyCopied={_handleNotifyCopied} value={action.clipboardData}>
              <ActionButton toastType={props.type} action={action} onClick={_handleActionClick} />
            </Clipboard>
          );
        }
        return null;
      })}
      {props.isCloseable && (
        <span onClick={_handleClose} className="toast-closeButton">
          <BenchlingIcon icon="close" dataTestAttrs={{component: 'Toast', element: 'closeIcon'}} />
        </span>
      )}
    </div>
  );
}
